/**
 * "No charge until after a session" reassurance (Sam, Oct 19 2026: talk-only
 * signups stopped at the add-card step without ever trying a card).
 *
 * Keep accurate to the code + Terms §ii (Session Fees):
 * - Saving a card is a Stripe SetupIntent: nothing is charged.
 * - Booking places an authorization hold; the PaymentIntent is captured by
 *   charge-after-session when the session ends (server/api-util/sessionCharge.js).
 * - Talker cancel of a scheduled session is free until CANCEL_FREE_NOTICE_HOURS
 *   before the start (util/cancelPolicy.js); inside that window it is charged in full.
 * - Scheduled talker no-show (listener joined) is charged in full.
 * - Talk Now is never charged if the talker doesn't join; a listener cancel or
 *   listener no-show never charges the talker.
 * - Card details sit with Stripe; we only keep brand + last 4 (trustCopy.js).
 */
import { cancelNoticeLabel } from './cancelPolicy';

export const CHARGE_HEADING = 'You won\u2019t be charged now';

export const chargeCardStepLines = () => [
  'Adding a card doesn\u2019t charge you. You\u2019re only charged after your session.',
  `Cancel a scheduled session free up to ${cancelNoticeLabel()} before.`,
  'Tap Start now and don\u2019t join? No charge.',
  'Your card is kept securely by Stripe. We never see your full card number.',
];

/** Bold lead of the short line. */
export const CHARGE_SHORT_LEAD = 'You won\u2019t be charged until after your session.';

/**
 * Second sentence of the short line.
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.startNow] Talk Now: free unless you join.
 * @param {boolean} [opts.card] Card-entry context: saving a card charges nothing.
 * @returns {string}
 */
export const chargeShortRest = ({ startNow = false, card = false } = {}) => {
  if (card) {
    return startNow
      ? 'Saving a card doesn\u2019t charge you, and Start now is free if you don\u2019t join.'
      : `Saving a card doesn\u2019t charge you. Free cancel up to ${cancelNoticeLabel()} before.`;
  }
  return startNow
    ? 'Don\u2019t join? No charge.'
    : `Free cancel up to ${cancelNoticeLabel()} before.`;
};

/** Signup checklist emphasis. */
export const CHARGE_SIGNUP_LEAD = 'Adding a card doesn\u2019t charge you.';
export const CHARGE_SIGNUP_REST = 'You\u2019re only charged after a session.';
